import { makeStyles } from '@material-ui/styles'
import React from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'

/**
 * DeletionProgress component displays a visual progress tracker for multi-step deletion operations.
 * Shows each step with appropriate icons (idle, pending, success, failed) and labels.
 *
 * @example
 * ```tsx
 * const [steps, setSteps] = useState([
 *   { step: 1, status: 'idle', label: 'Deleting Identity Provider' },
 *   { step: 2, status: 'pending', label: 'Deleting Federated Users' },
 *   { step: 3, status: 'success', label: 'Deleting Groups' },
 *   { step: 4, status: 'failed', label: 'Deleting Group Mappings' },
 * ])
 *
 * <DeletionProgress steps={steps} />
 * ```
 */

export interface DeletionStep {
  step: number
  status: 'idle' | 'pending' | 'success' | 'failed'
  label: string
}

interface Props {
  steps: DeletionStep[]
  className?: string
}

const progressIconByStatus = {
  success: 'check',
  pending: 'circle-notch',
  failed: 'xmark',
  idle: '', // No icon for idle state
} as const

const DeletionProgress: React.FC<Props> = ({ steps, className }) => {
  const classes = useStyles({})

  const statusClassMap = {
    success: classes.successIcon,
    pending: classes.pendingIcon,
    failed: classes.failedIcon,
  }

  return (
    <div className={`${classes.progressTable} ${className || ''}`}>
      {steps?.map((step) => (
        <React.Fragment key={step.step}>
          <Text variant="caption1">{step.step}.</Text>
          <Text variant="caption1">{step.label}</Text>
          <Text variant="body2" className={classes.centered}>
            <FontAwesomeIcon
              spin={step.status === 'pending'}
              className={statusClassMap[step?.status]}
            >
              {progressIconByStatus[step.status]}
            </FontAwesomeIcon>
          </Text>
        </React.Fragment>
      ))}
    </div>
  )
}

export default DeletionProgress
const useStyles = makeStyles<Theme>((theme) => ({
  progressTable: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content max-content',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 2),
    alignItems: 'center',
  },
  successIcon: {
    color: theme.components.badge.success.color,
    marginRight: theme.spacing(1),
  },
  pendingIcon: {
    color: theme.components.badge.warning.color,
    marginRight: theme.spacing(1),
  },
  failedIcon: {
    color: theme.components.badge.error.color,
    marginRight: theme.spacing(1),
  },
  idleIcon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
  },
  centered: {
    textAlign: 'center',
  },
}))
